import { BlindOffHandler, BlindOnHandler } from './handlers/BlindHandler';
import { CaptionHandler } from './handlers/CaptionHandler';
import { CastResumeHandler } from './handlers/CastResumeHandler';
import { ChatBlockCountryHandler } from './handlers/ChatBlockCountryHandler';
import { ChatMessageHandler, SponMessageHandler } from './handlers/ChatMessageHandler';
import {
  EventAnimCoinHandler,
  EventRankHandler,
  HeartIconVipHandler,
  IntroEffectHandler,
} from './handlers/EventHandler';
import { ExcelBroadcastHandler } from './handlers/ExcelBroadcastHandler';
import { FreezeOffHandler, FreezeOnHandler } from './handlers/FreezeHandler';
import { MediaUpdateHandler } from './handlers/MediaUpdateHandler';
import { MemberKickOutHandler } from './handlers/MemberKickOutHandler';
import { MissionHandler } from './handlers/MissionHandler';
import { ModifyRoomHandler } from './handlers/ModifyRoomHandler';
import { MuteOffHandler, MuteOnHandler } from './handlers/MuteHandler';
import { PersonalAdminHandler } from './handlers/PersonalAdminHandler';
import { PersonalManagerHandler } from './handlers/PersonalManagerHandler';
import { PersonalMissionHandler } from './handlers/PersonalMissionHandler';
import { PersonalOverlapLoginHandler } from './handlers/PersonalOverlapLoginHandler';
import { PersonalSupportHandler } from './handlers/PersonalSupportHandler';
import { PersonalUpdateUserInfoHandler } from './handlers/PersonalUpdateUserInfoHandler';
import { PersonalUserAlertHandler } from './handlers/PersonalUserAlertHandler';
import { PersonalUserStopHandler } from './handlers/PersonalUserStopHandler';
import { RoomEndHandler } from './handlers/RoomEndHandler';
import { ServiceCastBlockHandler } from './handlers/ServiceCastBlockHandler';
import { SoundOffHandler, SoundOnHandler } from './handlers/SoundHandler';
import { CreateNoticeHandler, DeleteNoticeHandler } from './handlers/SupporterHandler';
import { FanInHandler, FirstSponHandler, KingFanInHandler, RecommendHandler } from './handlers/TriggerHandler';
import { VoteHandler } from './handlers/VoteHandler';
import { DefaultMessageDispatcher } from './MessageDispatcher';
import type { MessageDispatcher, MessageHandlerActions } from './types';

/**
 * 메시지 핸들러들을 생성하고 디스패처에 등록
 *
 * @param actions 각 메시지 타입에 대한 action 정의
 *                - USE_DEFAULT: 핸들러의 기본 동작 실행
 *                - null | undefined: 메시지 무시 (skip)
 *                - function: 커스텀 동작 실행
 * @returns Public/Personal 디스패처
 */
export function createMessageHandlers(actions: MessageHandlerActions = {}) {
  const publicDispatcher: MessageDispatcher = new DefaultMessageDispatcher();
  const personalDispatcher: MessageDispatcher = new DefaultMessageDispatcher();

  // ==================== Public Message Handlers ====================

  publicDispatcher.register(new ChatMessageHandler(actions.onChatMessage));
  publicDispatcher.register(new SponMessageHandler(actions.onSponMessage));

  publicDispatcher.register(new CreateNoticeHandler(actions.onCreateNotice));
  publicDispatcher.register(new DeleteNoticeHandler(actions.onDeleteNotice));

  publicDispatcher.register(new FreezeOnHandler(actions.onFreezeOn));
  publicDispatcher.register(new FreezeOffHandler(actions.onFreezeOff));

  publicDispatcher.register(new MuteOnHandler(actions.onMuteOn));
  publicDispatcher.register(new MuteOffHandler(actions.onMuteOff));

  publicDispatcher.register(new MissionHandler(actions.onMission));

  publicDispatcher.register(new IntroEffectHandler(actions.onIntroEffect));
  publicDispatcher.register(new EventAnimCoinHandler(actions.onEventAnimCoin));
  publicDispatcher.register(new EventRankHandler(actions.onEventRank));
  publicDispatcher.register(new HeartIconVipHandler(actions.onHeartIconVip));

  publicDispatcher.register(new VoteHandler(actions.onVote));

  publicDispatcher.register(new ModifyRoomHandler(actions.onModifyRoom));

  publicDispatcher.register(new SoundOnHandler(actions.onSoundOn));
  publicDispatcher.register(new SoundOffHandler(actions.onSoundOff));

  publicDispatcher.register(new MemberKickOutHandler(actions.onMemberKickOut));

  publicDispatcher.register(new CastResumeHandler(actions.onCastResume));

  publicDispatcher.register(new ExcelBroadcastHandler(actions.onExcelBroadcast));

  publicDispatcher.register(new MediaUpdateHandler(actions.onMediaUpdate));

  publicDispatcher.register(new BlindOnHandler(actions.onBlindOn));
  publicDispatcher.register(new BlindOffHandler(actions.onBlindOff));

  publicDispatcher.register(new RoomEndHandler(actions.onRoomEnd));

  publicDispatcher.register(new RecommendHandler(actions.onRecommend));

  publicDispatcher.register(new KingFanInHandler(actions.onKingFanIn));

  publicDispatcher.register(new FanInHandler(actions.onFanIn));

  publicDispatcher.register(new CaptionHandler(actions.onCaption));

  publicDispatcher.register(new FirstSponHandler(actions.onFirstSpon));

  publicDispatcher.register(new ServiceCastBlockHandler(actions.onServiceCastBlock));
  publicDispatcher.register(new ChatBlockCountryHandler(actions.onChatBlockCountry));

  // ==================== Personal Message Handlers ====================

  personalDispatcher.register(new PersonalAdminHandler(actions.onPersonalAdmin));
  personalDispatcher.register(new PersonalSupportHandler(actions.onPersonalSupport));
  personalDispatcher.register(new PersonalUpdateUserInfoHandler(actions.onPersonalUpdateUserInfo));
  personalDispatcher.register(new PersonalUserAlertHandler(actions.onPersonalUserAlert));
  personalDispatcher.register(new PersonalUserStopHandler(actions.onPersonalUserStop));
  personalDispatcher.register(new PersonalOverlapLoginHandler(actions.onPersonalOverlapLogin));
  personalDispatcher.register(new PersonalMissionHandler(actions.onPersonalMission));
  personalDispatcher.register(new PersonalManagerHandler(actions.onPersonalManager));

  return {
    publicDispatcher,
    personalDispatcher,
  };
}
